import React, { useState, useEffect } from 'react';
import { agentAPI, dashboardAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import { FiTrendingUp, FiUsers, FiFileText, FiCoffee, FiActivity, FiBriefcase, FiGift, FiAlertCircle, FiBook, FiMessageSquare } from 'react-icons/fi';

const StatCard = ({ icon, title, value, color, subtitle }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    pink: 'bg-pink-100 text-pink-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <SafeIcon icon={icon} className="w-5 h-5" />
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

const LineDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentId, setAgentId] = useState(null);

  // Events state
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [events, setEvents] = useState({ birthdays: [], expiringLicenses: [] });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const { data: agent } = await agentAPI.getMyProfile();
        if (agent) {
          setAgentId(agent.id);
          const { data: dashboardStats } = await dashboardAPI.getLineDashboardStats(agent.id);
          setStats(dashboardStats);
        } else {
          setError('ไม่พบข้อมูลโปรไฟล์ตัวแทน');
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data", error);
        setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (agentId) {
      const fetchEvents = async () => {
        try {
          const { data } = await dashboardAPI.getLineDashboardEvents(agentId, selectedMonth);
          setEvents(data);
        } catch (error) {
          console.error("Failed to fetch dashboard events", error);
        }
      };
      fetchEvents();
    }
  }, [agentId, selectedMonth]);

  const months = [
    { value: 1, label: 'มกราคม' },
    { value: 2, label: 'กุมภาพันธ์' },
    { value: 3, label: 'มีนาคม' },
    { value: 4, label: 'เมษายน' },
    { value: 5, label: 'พฤษภาคม' },
    { value: 6, label: 'มิถุนายน' },
    { value: 7, label: 'กรกฎาคม' },
    { value: 8, label: 'สิงหาคม' },
    { value: 9, label: 'กันยายน' },
    { value: 10, label: 'ตุลาคม' },
    { value: 11, label: 'พฤศจิกายน' },
    { value: 12, label: 'ธันวาคม' },
  ];

  const formatCurrency = (value) => 
    new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(value);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  if (!stats) {
     return <div className="p-8 text-center text-gray-500">ไม่พบข้อมูล</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">แดชบอร์ด</h1>
        <p className="text-gray-600">ภาพรวมผลงานของคุณและทีมงาน</p>
      </div>

      <div className="space-y-8">
        {/* Personal Stats */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">ภาพรวมส่วนตัว</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={FiUsers} title="ทีมงาน" value={stats.personal.downlines} color="blue" subtitle="คน" />
            <StatCard icon={FiFileText} title="กรมธรรม์ของฉัน" value={stats.personal.policies} color="green" subtitle="ฉบับ" />
            <StatCard icon={FiTrendingUp} title="ยอดขายปีนี้" value={formatCurrency(stats.personal.yearlySales)} color="purple" />
            <StatCard icon={FiCoffee} title="การโค้ช" value={stats.personal.coachingSessions} color="orange" subtitle="ครั้งในปีนี้" />
          </div>
        </div>

        {/* Team Stats (Only if has downlines) */}
        {stats.personal.downlines > 0 && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">ภาพรวมทีม (รวมส่วนตัว)</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
              <StatCard icon={FiActivity} title="ยอดขายรวมทีม" value={formatCurrency(stats.team.totalSales)} color="indigo" subtitle="รวมยอดของคุณ" />
              <StatCard icon={FiBriefcase} title="กรมธรรม์รวมทีม" value={stats.team.totalPolicies} color="pink" subtitle="ฉบับ" />
              <StatCard icon={FiUsers} title="สมาชิกทีมทั้งหมด" value={stats.team.totalMembers} color="blue" subtitle="คน" />
              <StatCard icon={FiMessageSquare} title="การโค้ชรวมทีม" value={stats.team.totalCoaching} color="orange" subtitle="ครั้ง" />
              <StatCard icon={FiBook} title="การอบรมรวมทีม" value={stats.team.totalTraining} color="green" subtitle="ครั้ง" />
            </div>

            {/* Birthdays & Expirations (Team) */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-0">ปฏิทินทีมงาน</h3>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="mt-1 block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  >
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Birthdays */}
                  <div className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center mb-4 text-indigo-600">
                      <SafeIcon icon={FiGift} className="w-5 h-5 mr-2" />
                      <h4 className="font-medium text-gray-900">วันเกิดทีมงาน</h4>
                    </div>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {events.birthdays.length > 0 ? (
                        events.birthdays.map((agent) => (
                          <div key={agent.id} className="flex items-center bg-white p-3 rounded shadow-sm">
                            {agent.profileImage ? (
                              <img src={agent.profileImage} alt="" className="w-8 h-8 rounded-full object-cover mr-3" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500 mr-3 text-xs">
                                {agent.fullName.charAt(0)}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{agent.fullName}</p>
                              <p className="text-xs text-gray-500">วันที่ {agent.day}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">ไม่มีวันเกิดในเดือนนี้</p>
                      )}
                    </div>
                  </div>

                  {/* License Expirations */}
                  <div className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center mb-4 text-orange-600">
                      <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
                      <h4 className="font-medium text-gray-900">ใบอนุญาตหมดอายุ</h4>
                    </div>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {events.expiringLicenses.length > 0 ? (
                        events.expiringLicenses.map((agent) => (
                          <div key={agent.id} className="flex items-center bg-white p-3 rounded shadow-sm border-l-4 border-orange-400">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{agent.fullName}</p>
                              <p className="text-xs text-gray-500">หมดอายุ: {agent.date}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">ไม่มีใบอนุญาตหมดอายุในเดือนนี้</p>
                      )}
                    </div>
                  </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineDashboard;
